import { useMemo } from 'react';
import { useBudgetSummary } from '../../hooks/api/budgetBillingConfig';
import { getContiguousMonths } from './helpers';

/**
 * CALL-816: the months this version's budget is actually FUNDED for — the entered Ist-Budget months.
 *
 * This is the axis the row status and its acknowledgement are both judged on, so it must not be the
 * view: scrolling the year or setting a date range would otherwise change what "incomplete" (and
 * therefore what "acknowledged") means, and a row would have to be re-acknowledged on every window.
 *
 * Funded, not merely touched: without a budget entered for a month, that month's rates and volumes are
 * not something anyone signed up to deliver, so flagging the row for them is noise. A month whose
 * budget was explicitly saved as 0 counts as unfunded, matching how the backend's own period rule reads
 * a zero baseline.
 *
 * The span is CONTIGUOUS from the first funded month to the last, so a hole in the middle of the funded
 * range stays inside the period and therefore stays detectable — that hole is the case the warning
 * exists for.
 *
 * Read off the summary, which BudgetSummary already fetches on this page (React Query serves it from
 * cache), and deliberately off the SERVER's summary rather than the page's unsaved Ist-Budget edits: an
 * acknowledgement is a stored record shared by every user of the version, so a half-typed budget cell
 * must not move what it covers — and keeping the pending set out of the deps keeps this identity stable
 * for the memo chain that hangs off it.
 */
export const useBudgetPeriodMonths = (versionId: number | undefined): string[] => {
    const { data: summary } = useBudgetSummary(versionId);

    return useMemo(() => {
        const funded = summary.filter((row) => Number(row.budget) !== 0).map((row) => row.month);
        // getContiguousMonths falls back to the CURRENT YEAR on an empty list, which would invent a
        // period out of nothing — so only hand it a non-empty set.
        if (funded.length) return getContiguousMonths(funded);

        // No budget entered anywhere yet (a fresh draft): fall back to the summary's own months, i.e.
        // the span the backend derives from whatever WAS entered — rates, manual volumes, on-top. An
        // empty axis would otherwise report every row as having no data at all.
        return summary.map((row) => row.month);
    }, [summary]);
};
